// UIController - Handles all DOM interactions, panels, controls, and state synchronization
// Mellizos Brand — Black / Orange, Atlas sidebar, aurora backdrops

import { state, actions, getters } from '../store/state.js';
import { api } from '../api/client.js';
import { updateCountryPins, CATEGORY_COLORS } from '../globe/GlobeScene.js';
import { isFollowingCountry, toggleCountry, getFollows, onFollowsChange } from '../features/follow.js';
import { annotate, explain } from '../features/glossary.js';
import { shareStory } from '../features/share.js';
import { renderSnapshot } from '../features/snapshot.js';
import { renderCountryResearch } from '../features/countryResearch.js';
import { t, stories, countryName, catName, contName } from '../i18n/index.js';

// DOM element references
let elements = {};

// Country sidebar state
let sidebarCountries = [];
let sidebarSearchTerm = '';
let sidebarActiveContinent = null;

// Default categories for legend before DB load
const DEFAULT_LEGEND = [
    { slug: 'finance', name: 'Finance', color: '#00D97E', icon: '💰' },
    { slug: 'government', name: 'Government', color: '#3B82F6', icon: '🏛️' },
    { slug: 'technology', name: 'Technology', color: '#A855F7', icon: '💻' },
    { slug: 'health', name: 'Health', color: '#F43F5E', icon: '🏥' },
    { slug: 'science', name: 'Science', color: '#14B8A6', icon: '🔬' },
    { slug: 'sports', name: 'Sports', color: '#F59E0B', icon: '⚽' },
    { slug: 'entertainment', name: 'Entertainment', color: '#EC4899', icon: '🎭' },
    { slug: 'environment', name: 'Environment', color: '#22C55E', icon: '🌿' },
    { slug: 'world', name: 'World', color: '#64748B', icon: '🌍' },
    { slug: 'culture', name: 'Culture', color: '#FB923C', icon: '🏛️' },
    { slug: 'education', name: 'Education', color: '#6366F1', icon: '🎓' },
    { slug: 'lifestyle', name: 'Lifestyle', color: '#F43F5E', icon: '☕' },
];

// Continent order for sidebar
const CONTINENT_ORDER = ['Africa', 'Asia', 'Europe', 'North America', 'Oceania', 'South America'];

// Category colors — normalise THREE numeric hex (0xD4A843) and/or CSS hex
// strings ('#...') down to CSS `#rrggbb` for inline `style=""` use.
const CAT_CSS_VAR = Object.fromEntries(
    Object.entries(CATEGORY_COLORS).map(([slug, color]) => [slug, toCssHex(color)]),
);

function toCssHex(color) {
    if (typeof color === 'number') {
        return `#${color.toString(16).padStart(6, '0')}`;
    }
    if (typeof color === 'string' && color.startsWith('#')) {
        return color;
    }
    return '#666666';
}

function categoryColor(cat) {
    if (cat && typeof cat.color === 'string' && cat.color.startsWith('#')) return cat.color;
    return CAT_CSS_VAR[cat?.slug] || '#666666';
}

// ============================================================
// Signature — deterministic hue pair per country (mirrors app README verbatim)
// ============================================================
function signature(iso2) {
    const key = (iso2 || 'AA').toUpperCase();
    let h = 0x4e79a7;
    for (let i = 0; i < key.length; i++) {
        h = ((h << 5) - h + key.charCodeAt(i)) | 0;
    }
    const a = (h >>> 0) % 360;
    const b = ((h * 2654435769) >>> 0) % 360;
    return { a, b };
}

// ============================================================
// Initialize UI
// ============================================================
export function initUI() {
    cacheElements();
    bindEvents();
    renderInitialUI();
    setupStateWatchers();
    setupFollow();
    setupRotateButton();
    console.log('[ui] Initialized');
}

function cacheElements() {
    // Panels / overlays
    elements.countryPanel = document.getElementById('country-panel');
    elements.legendPanel = document.querySelector('.legend-panel');
    elements.loadingOverlay = document.querySelector('.loading-overlay');
    elements.errorState = document.querySelector('.error-state');

    // Country panel elements
    elements.panelFlag = document.getElementById('panel-flag');
    elements.panelCountryName = document.getElementById('panel-country-name');
    elements.panelContinent = document.getElementById('panel-continent');
    elements.panelCloseBtn = document.getElementById('panel-close');
    elements.panelNewsList = document.getElementById('panel-news-list');
    elements.panelSponsorshipCta = document.getElementById('panel-sponsorship-cta');
    elements.panelWindowLabel = document.getElementById('panel-window-label');
    elements.panelFollow = document.getElementById('panel-follow');

    // Sidebar
    elements.sidebar = document.querySelector('.sidebar');
    elements.sidebarContent = document.getElementById('country-sidebar');
    elements.sidebarSearch = document.getElementById('country-search');
    elements.sidebarBackdrop = document.getElementById('sidebar-backdrop');
    elements.sidebarCount = document.getElementById('sidebar-count');
    elements.sidebarToggle = document.getElementById('sidebar-toggle');

    // Filters
    elements.continentSelect = document.getElementById('continent-filter');
    elements.windowControl = document.getElementById('window-control');

    // Header / category strip
    elements.categoryStrip = document.getElementById('category-strip');
    elements.timeValue = document.getElementById('time-value');

    // Stage title
    elements.stageTitle = document.querySelector('.stage-title');
    elements.stageTitleMain = document.getElementById('stage-title-main');
    elements.stageTitleSub = document.getElementById('stage-title-sub');

    // Country hero
    elements.countryHero = document.getElementById('country-hero');
    elements.auroraA = document.getElementById('aurora-a');
    elements.auroraB = document.getElementById('aurora-b');
    elements.heroImage = document.getElementById('hero-image');

    // Panel backdrop (tinted by the same signature)
    elements.panelBackdrop = document.querySelector('.country-panel-backdrop');

    // Legend
    elements.legendContainer = document.getElementById('legend-items');

    // Error retry
    elements.errorRetryBtn = document.getElementById('error-retry');
}

// ============================================================
// Event binding
// ============================================================
function bindEvents() {
    // Country panel close
    elements.panelCloseBtn?.addEventListener('click', () => actions.closePanel());

    // Sidebar search
    elements.sidebarSearch?.addEventListener('input', (e) => {
        sidebarSearchTerm = e.target.value.toLowerCase();
        renderSidebarCountries();
    });

    // Sidebar backdrop (mobile) close
    elements.sidebarBackdrop?.addEventListener('click', () => {
        closeMobileSidebar();
    });

    // Sidebar toggle (mobile)
    elements.sidebarToggle?.addEventListener('click', () => {
        const open = elements.sidebar?.classList?.toggle('open');
        elements.sidebarBackdrop?.classList?.toggle('visible', open);
        elements.sidebarToggle?.setAttribute('aria-expanded', String(open));
    });

    // Drawer tabs (phones): country list or site menu
    document.querySelectorAll('.sidebar-tab').forEach((tab) =>
        tab.addEventListener('click', () => {
            const menu = tab.dataset.tab === 'menu';
            elements.sidebar?.classList?.toggle('show-menu', menu);
            document.querySelectorAll('.sidebar-tab').forEach((x) => {
                x.classList.toggle('active', x === tab);
                x.setAttribute('aria-selected', String(x === tab));
            });
        }),
    );

    // Region select
    elements.continentSelect?.addEventListener('change', (e) => {
        actions.setContinentFilter(e.target.value);
        sidebarActiveContinent = e.target.value === 'all' ? null : e.target.value;
        renderSidebarCountries();
    });

    // Window segmented control
    elements.windowControl?.addEventListener('click', (e) => {
        const btn = e.target.closest('.window-btn');
        if (!btn) return;
        const range = btn.dataset.range;
        if (!range) return;
        actions.setTimeRange(range);
        setActiveWindowBtn(range);
        refreshNewsForCurrentView();
    });

    // Error retry
    elements.errorRetryBtn?.addEventListener('click', () => {
        window.location.reload();
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            actions.closePanel();
            closeMobileSidebar();
        }
        if (e.key === ' ' && e.target === document.body) {
            e.preventDefault();
            state.globe.autoRotate = !state.globe.autoRotate;
        }
    });

    // Click outside panel to close (mobile). Skipped for:
    //  - clicks that *open* the panel (sidebar rows, the globe canvas) — they
    //    bubble here too and would close it in the same tick;
    //  - the header (category pills, window buttons) and sidebar controls —
    //    changing a filter should refilter the open panel, not dismiss it.
    document.addEventListener('click', (e) => {
        if (
            state.ui.panelOpen &&
            elements.countryPanel &&
            !elements.countryPanel.contains(e.target) &&
            !e.target.closest('.country-item, canvas, .header, .sidebar')
        ) {
            actions.closePanel();
        }
    });
}

// ============================================================
// Initial render
// ============================================================
function renderInitialUI() {
    // Footer year
    document.querySelectorAll('[data-year]').forEach((el) => {
        el.textContent = String(new Date().getFullYear());
    });

    // Region select
    if (elements.continentSelect) {
        elements.continentSelect.innerHTML =
            `<option value="all">${escapeHtml(t('filter.allRegions'))}</option>` +
            CONTINENT_ORDER.map((c) => `<option value="${c}">${escapeHtml(contName(c))}</option>`).join('');
    }

    // Sync window segmented control to state (default 7D / 24h)
    setActiveWindowBtn(state.controls.timeRange);

    // Populate category pills
    populateCategoryFilter();

    // Render legend
    renderLegend();

    // Render sidebar countries
    renderSidebarCountries();
}

// ============================================================
// State watchers
// ============================================================
function setupStateWatchers() {
    watchState(
        () => state.ui.loading,
        (loading) => {
            if (elements.loadingOverlay) {
                elements.loadingOverlay.style.display = loading ? 'flex' : 'none';
            }
        },
    );

    watchState(
        () => state.ui.error,
        (error) => {
            if (elements.errorState) {
                elements.errorState.style.display = error ? 'flex' : 'none';
                if (error) {
                    const msgEl = elements.errorState.querySelector('.error-message');
                    if (msgEl) msgEl.textContent = error;
                }
            }
        },
    );

    watchState(
        () => state.ui.selectedCountry,
        (iso2) => {
            if (iso2) {
                openCountryPanel(iso2);
            } else {
                closeCountryPanel();
                clearCountryBackdrop();
            }
            renderSidebarCountries();
        },
    );

    watchState(
        () => state.ui.hoveredCountry,
        (iso2) => {
            updateHoverTooltip(iso2);
        },
    );

    watchState(
        () => state.countries,
        (countries) => {
            if (countries.length > 0) {
                updateCountryPins(countries);
                sidebarCountries = countries;
                renderSidebarCountries();
                loadCountryStats();
            }
        },
    );

    watchState(
        () => state.countryStats,
        () => {
            updateCountryPins(state.countries);
            renderSidebarCountries();
        },
    );

    watchState(
        () => state.categories,
        (categories) => {
            populateCategoryFilter();
            renderLegend();
        },
    );

    watchState(
        () => state.news.latest,
        () => {
            if (state.ui.selectedCountry) {
                renderCountryNews(state.ui.selectedCountry);
            }
            updateCountryPins(state.countries);
            renderSidebarCountries();
        },
    );


    watchState(
        () => state.controls.continentFilter,
        (continent) => {
            if (elements.continentSelect) elements.continentSelect.value = continent;
            sidebarActiveContinent = continent === 'all' ? null : continent;
            renderSidebarCountries();
        },
    );

    watchState(
        () => state.controls.categoryFilter,
        () => {
            syncCategoryPills();
            refreshNewsForCurrentView(); // panel: re-fetch this category from the server
            loadCountryStats(); // globe + sidebar: only countries with this category
        },
    );

    watchState(
        () => state.controls.timeRange,
        (range) => {
            setActiveWindowBtn(range);
            loadCountryStats();
            const panelWinLabel = elements.panelWindowLabel;
            if (panelWinLabel) panelWinLabel.textContent = rangeLabel(range);
        },
    );

    // Update time-value in cycle indicator
    watchState(
        () => state.ui.lastUpdated,
        () => {
            if (elements.timeValue) {
                elements.timeValue.textContent = formatRelativeTime(state.ui.lastUpdated);
            }
        },
    );
}

// Simple watch helper
function watchState(getter, callback) {
    let oldValue = getter();
    const check = () => {
        const newValue = getter();
        if (newValue !== oldValue) {
            callback(newValue, oldValue);
            oldValue = newValue;
        }
        requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
}

// ============================================================
// Country Panel
// ============================================================

function openCountryPanel(iso2) {
    const country = state.countries.find((c) => c.iso2 === iso2);
    if (!country) return;

    // Update header
    if (elements.panelFlag) elements.panelFlag.textContent = country.flag_emoji || '🏳️';
    if (elements.panelCountryName) elements.panelCountryName.textContent = countryName(country.iso2, country.name);
    if (elements.panelContinent) elements.panelContinent.textContent = contName(country.continent);
    syncFollowButton(country.iso2);
    // Link to the country's own page (server-rendered, shareable, indexed by Google)
    const pageLink = document.getElementById('panel-page');
    if (pageLink) {
        const slug = String(country.name).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
            .replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        pageLink.href = `/country/${slug}`;
    }

    // Activate country backdrop (aurora + optional hero image)
    activateCountryBackdrop(country);

    // Stage title
    updateStageTitle(country);

    // Render whatever is cached from the global feeds, then fetch the full
    // per-country list (the startup feeds only hold the latest ~30 worldwide)
    renderCountryNews(iso2);
    loadCountryNews(iso2);

    // Investor snapshot (official yearly figures + plain-language reading)
    renderSnapshot(iso2);
    // Research papers about this country (hidden when there are none)
    renderCountryResearch(iso2, country.name);

    // Show/hide sponsorship CTA
    const hasNews = country.has_news === true || country.has_news === 1;
    if (elements.panelSponsorshipCta) {
        elements.panelSponsorshipCta.style.display = hasNews ? 'none' : 'block';
    }

    // Panel window label
    if (elements.panelWindowLabel) {
        elements.panelWindowLabel.textContent = rangeLabel(state.controls.timeRange);
    }

    // Show panel
    if (elements.countryPanel) {
        elements.countryPanel.classList.add('open');
    }

    // Highlight active row in sidebar
    renderSidebarCountries();
}

function closeCountryPanel() {
    if (elements.countryPanel) {
        elements.countryPanel.classList.remove('open');
    }
}

// ============================================================
// Country backdrop — aurora signature + optional hero image
// ============================================================

function activateCountryBackdrop(country) {
    const hero = elements.countryHero;
    if (!hero) return;

    // Determine aurora source: hero_image_url if present, else signature
    const sig = signature(country.iso2);
    const heroUrl = country.hero_image_url || null;

    // Aurora gradient layers
    if (elements.auroraA) {
        elements.auroraA.style.background = `radial-gradient(ellipse at 35% 45%, hsla(${sig.a}, 52%, 24%, 0.38) 0%, hsla(${sig.a}, 32%, 12%, 0) 68%)`;
    }
    if (elements.auroraB) {
        elements.auroraB.style.background = `radial-gradient(ellipse at 65% 55%, hsla(${sig.b}, 44%, 20%, 0.30) 0%, hsla(${sig.b}, 28%, 10%, 0) 64%)`;
    }

    // Hero image (if DB has a curated one)
    if (elements.heroImage) {
        if (heroUrl) {
            elements.heroImage.style.backgroundImage = `url("${heroUrl}")`;
            elements.heroImage.style.opacity = '0.35';
        } else {
            elements.heroImage.style.backgroundImage = 'none';
            elements.heroImage.style.opacity = '0';
        }
    }

    hero.classList.add('visible');

    // Panel backdrop shares the aurora tint (no image, just the blend)
    if (elements.panelBackdrop) {
        elements.panelBackdrop.innerHTML = `
            <div class="cpb-aurora" style="background:radial-gradient(ellipse at 30% 40%, hsla(${sig.a}, 52%, 24%, 0.40) 0%, hsla(${sig.a}, 30%, 10%, 0) 62%)"></div>
            <div class="cpb-aurora cpb-aurora-b" style="background:radial-gradient(ellipse at 70% 60%, hsla(${sig.b}, 44%, 20%, 0.32) 0%, hsla(${sig.b}, 26%, 9%, 0) 58%)"></div>
        `;
    }
}

function clearCountryBackdrop() {
    const hero = elements.countryHero;
    if (!hero) return;
    hero.classList.remove('visible');
    // Stage title
    if (elements.stageTitleMain) elements.stageTitleMain.textContent = t('stage.title');
    if (elements.stageTitleSub) elements.stageTitleSub.textContent = '';
    if (elements.stageTitle) elements.stageTitle.classList.remove('has-country');
}

function updateStageTitle(country) {
    if (elements.stageTitleMain) elements.stageTitleMain.textContent = countryName(country.iso2, country.name);
    if (elements.stageTitleSub) {
        // Same per-window count as the Atlas sidebar (getters.countryNews is
        // a cached computed and can be stale or only partly loaded here)
        const newsCount = state.countryStats?.[country.iso2]?.count || 0;
        const region = country.continent ? contName(country.continent) : '';
        const parts = [region];
        if (newsCount > 0) parts.push(stories(newsCount));
        elements.stageTitleSub.textContent = parts.filter(Boolean).join(' · ');
    }
    if (elements.stageTitle) elements.stageTitle.classList.add('has-country');
}

// ============================================================
// Window segmented control
// ============================================================

function setActiveWindowBtn(range) {
    const ctrl = elements.windowControl;
    if (!ctrl) return;
    ctrl.querySelectorAll('.window-btn').forEach((btn) => {
        const active = btn.dataset.range === range;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', active);
    });
}

function rangeLabel(range) {
    return (
        { '24h': t('range.24h'), '7d': t('range.7d'), '30d': t('range.30d'), all: t('range.all') }[
            range
        ] || range
    );
}

// ============================================================
// Category filter — dot-prefixed pills
// ============================================================

function populateCategoryFilter() {
    const strip = elements.categoryStrip;
    if (!strip) return;

    const categories = state.categories
        .filter((c) => c.is_active)
        .sort((a, b) => a.display_order - b.display_order);

    if (categories.length === 0) return;

    // Skip re-render if same count
    const existingPills = strip.querySelectorAll(
        '.category-pill[data-category]:not([data-category="all"])',
    );
    if (existingPills.length === categories.length) return;

    // Preserve the "All" pill (it ships in index.html)
    const allPill =
        strip.querySelector('.category-pill[data-category="all"]') ||
        Object.assign(document.createElement('button'), {
            className: 'category-pill',
            innerHTML: `<span class="pill-dot" aria-hidden="true"></span><span>${t('filter.all')}</span>`,
        });
    allPill.dataset.category = 'all';
    strip.innerHTML = '';
    strip.appendChild(allPill);

    // One delegated handler for every pill. Per-pill listeners missed the
    // static "All stories" button in index.html, so it could never be
    // re-selected once another category was chosen.
    if (!strip.dataset.bound) {
        strip.dataset.bound = '1';
        strip.addEventListener('click', (e) => {
            const pill = e.target.closest('.category-pill');
            if (!pill || !strip.contains(pill)) return;
            const slug = pill.dataset.category || 'all';
            if (slug !== state.controls.categoryFilter) actions.setCategoryFilter(slug);
        });
    }

    for (const cat of categories) {
        const pill = document.createElement('button');
        pill.className = 'category-pill';
        pill.dataset.category = cat.slug;
        pill.setAttribute('aria-pressed', 'false');
        const color = categoryColor(cat);
        pill.innerHTML = `
            <span class="pill-dot" style="background:${color}" aria-hidden="true"></span>
            <span>${escapeHtml(catName(cat.slug, cat.name))}</span>
        `;
        strip.appendChild(pill);
    }

    syncCategoryPills();
}

/** Active pill always mirrors state (also after restoring saved prefs). */
function syncCategoryPills() {
    const strip = elements.categoryStrip;
    if (!strip) return;
    const current = state.controls.categoryFilter || 'all';
    strip.querySelectorAll('.category-pill').forEach((p) => {
        const active = (p.dataset.category || 'all') === current;
        p.classList.toggle('active', active);
        p.setAttribute('aria-pressed', String(active));
    });
}

// ============================================================
// Legend
// ============================================================

function renderLegend() {
    if (!elements.legendContainer) return;

    const categories =
        state.categories.length > 0
            ? state.categories
                  .filter((c) => c.is_active)
                  .sort((a, b) => a.display_order - b.display_order)
            : DEFAULT_LEGEND;

    elements.legendContainer.innerHTML = categories
        .map(
            (cat) => `
            <span class="legend-item">
                <span class="legend-color" style="background: ${categoryColor(cat)}"></span>
                <span>${escapeHtml(catName(cat.slug, cat.name))}</span>
            </span>
        `,
        )
        .join('');
}

// ============================================================
// Country Sidebar — 44px rows, category dots, meta, count
// ============================================================

function renderSidebarCountries() {
    if (!elements.sidebarContent) return;

    // Per-country counts for the current window come from /api/countries/stats
    const stats = state.countryStats || {};

    const filtered = sidebarCountries
        .filter((c) => {
            const matchesSearch =
                c.name.toLowerCase().includes(sidebarSearchTerm) ||
                countryName(c.iso2, c.name).toLowerCase().includes(sidebarSearchTerm) ||
                c.iso2.toLowerCase().includes(sidebarSearchTerm) ||
                (c.flag_emoji && c.flag_emoji.includes(sidebarSearchTerm));
            const matchesContinent =
                !sidebarActiveContinent || c.continent === sidebarActiveContinent;
            return matchesSearch && matchesContinent;
        })
        .sort((a, b) => {
            if (a.continent !== b.continent) return a.continent.localeCompare(b.continent);
            return a.name.localeCompare(b.name);
        });

    // Group by continent
    const byContinent = {};
    for (const c of filtered) {
        if (!byContinent[c.continent]) byContinent[c.continent] = [];
        byContinent[c.continent].push(c);
    }

    let visibleTotal = 0;

    // "Following" group pinned at the top (features/follow.js)
    const followed = getFollows().countries;
    const followingRows = filtered.filter((c) => followed.includes(c.iso2));

    const html = (followingRows.length
        ? `<div class="sidebar-group sidebar-group--following">
                <span class="sidebar-group-label">★ ${escapeHtml(t('followingGroup'))} <span class="sidebar-group-count">${followingRows.length}</span></span>
                <ul class="country-list">${followingRows.map((c) => sidebarRow(c, stats)).join('')}</ul>
           </div>`
        : '') + CONTINENT_ORDER.filter((cont) => byContinent[cont] && byContinent[cont].length > 0)
        .map((cont) => {
            const items = byContinent[cont];
            const rows = items
                .map((country) => {
                    visibleTotal++;
                    return sidebarRow(country, stats);
                })
                .join('');

            return `
                <div class="sidebar-group">
                    <span class="sidebar-group-label">
                        ${escapeHtml(contName(cont))}
                        <span class="sidebar-group-count">${items.length}</span>
                    </span>
                    <ul class="country-list">${rows}</ul>
                </div>
            `;
        })
        .join('');

    elements.sidebarContent.innerHTML =
        html || '<p class="news-empty">No countries match your search.</p>';

    // Update count badge
    if (elements.sidebarCount) {
        elements.sidebarCount.textContent = `${visibleTotal}/${sidebarCountries.length}`;
    }

    // Bind click events
    elements.sidebarContent.querySelectorAll('.country-item').forEach((item) => {
        item.addEventListener('click', () => {
            const iso2 = item.dataset.iso2;
            actions.selectCountry(iso2);
            closeMobileSidebar();
        });
        item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                item.click();
            }
        });
    });
}

/** One country row in the Atlas sidebar. */
function sidebarRow(country, stats) {
                    const s = stats[country.iso2];
                    const count = s ? s.count : 0;
                    const isActive = state.ui.selectedCountry === country.iso2;
                    const isQuiet = count === 0;
                    // Top 3 category dots
                    const topCats = s ? s.categories.map((c) => [c.slug, c.count]) : [];
                    // Lead category meta string
                    const leadCat = topCats.length > 0 ? topCats[0][0] : null;
                    const metaStr =
                        count > 0
                            ? `${stories(count)}${leadCat ? ' · ' + catName(leadCat, capitalize(leadCat)) : ''}`
                            : '';

                    return `
                    <li class="country-item ${isActive ? 'active' : ''} ${isQuiet ? 'quiet' : ''}"
                        data-iso2="${country.iso2}"
                        role="button"
                        tabindex="0"
                        aria-label="${country.flag_emoji || ''} ${countryName(country.iso2, country.name)} ${metaStr || t('noStories')}"
                        aria-pressed="${isActive}">
                        <span class="country-item-flag" aria-hidden="true">${country.flag_emoji || '🏳️'}</span>
                        <span class="country-item-copy">
                            <span class="country-item-name">${escapeHtml(countryName(country.iso2, country.name))}</span>
                            ${metaStr ? `<span class="country-item-meta">${metaStr}</span>` : ''}
                        </span>
                        ${
                            topCats.length > 0
                                ? `
                            <span class="country-item-sig" aria-hidden="true">
                                ${topCats.map(([slug]) => `<span class="sig-dot" style="background:${categoryColor({ slug })}"></span>`).join('')}
                            </span>
                        `
                                : ''
                        }
                    </li>
                `;
                }

function capitalize(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

function closeMobileSidebar() {
    elements.sidebar?.classList?.remove('open');
    elements.sidebarBackdrop?.classList?.remove('visible');
    elements.sidebarToggle?.setAttribute('aria-expanded', 'false');
}

// ============================================================
// Country Panel — news
// ============================================================

function renderCountryNews(iso2) {
    if (!elements.panelNewsList) return;

    // Read the Map directly: getters.countryNews is a cached computed that
    // can't see Map.set() from loadCountryNews, so it would stay stale.
    const news = state.news.byCountry.get(iso2.toUpperCase()) || [];
    const filtered = filterNews(news);

    if (filtered.length === 0) {
        elements.panelNewsList.innerHTML = '';
        // Show quiet CTA
        if (elements.panelSponsorshipCta) elements.panelSponsorshipCta.style.display = 'block';
        return;
    }

    if (elements.panelSponsorshipCta) elements.panelSponsorshipCta.style.display = 'none';

    elements.panelNewsList.innerHTML = filtered
        .map((article) => {
            const catColor = categoryColor({
                slug: article.category_slug,
                color: article.category_color,
            });
            const url = safeUrl(article.url);
            const lang = (article.language || 'en').toLowerCase();
            const foreign = lang !== 'en';
            // Free translation: the reader opts in, Google renders the source page.
            const translateUrl = url
                ? `https://translate.google.com/translate?sl=auto&tl=en&u=${encodeURIComponent(article.url)}`
                : null;
            return `
            <article class="news-card" data-article-id="${Number(article.id) || ''}">
                <div class="news-card-header">
                    <span class="news-card-cat" style="color:${catColor}">
                        <span class="dot" style="background:${catColor}"></span>
                        ${escapeHtml(catName(article.category_slug, article.category_name || ''))}
                        ${foreign ? `<span class="news-card-lang" title="Original language">${escapeHtml(lang.toUpperCase())}</span>` : ''}
                    </span>
                    <time class="news-card-time" datetime="${escapeHtml(article.source_published_at)}">${formatRelativeTime(article.source_published_at)}</time>
                </div>
                <h4 class="news-card-title">${url ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${escapeHtml(article.title)}</a>` : escapeHtml(article.title)}</h4>
                ${article.summary ? `<p class="news-card-summary" title="Click to expand">${annotate(escapeHtml(article.summary))}</p>` : ''}
                <div class="news-card-footer">
                    <span class="news-card-source">${escapeHtml(article.source_name)}</span>
                    <span class="news-card-importance">
                        <span class="importance-track"><span class="importance-fill" style="width:${Math.min(100, Math.round(article.importance_score))}%"></span></span>
                        ${Math.round(article.importance_score)}
                    </span>
                </div>
                ${
                    url
                        ? `<div class="news-card-actions">
                    <a class="news-card-read" href="${url}" target="_blank" rel="noopener noreferrer">${escapeHtml(t('readAt', { source: article.source_name }))} <span aria-hidden="true">↗</span></a>
                    ${foreign ? `<a class="news-card-translate" href="${translateUrl}" target="_blank" rel="noopener noreferrer">${escapeHtml(t('translate'))} <span aria-hidden="true">↗</span></a>` : ''}
                </div>`
                        : ''
                }
                <div class="news-card-tools">
                    <button type="button" class="news-card-explain" aria-expanded="false">${escapeHtml(t('explain'))}</button>
                    <button type="button" class="news-card-share">${escapeHtml(t('share'))}</button>
                </div>
                <div class="news-card-explainer" hidden></div>
            </article>
        `;
        })
        .join('');

    // "Showing X of Y" + Load more, when the server has more than we've fetched
    const key = `${iso2.toUpperCase()}|${state.controls.timeRange}|${state.controls.categoryFilter}`;
    if (countryPage.key === key && countryPage.total > 0) {
        const more = countryPage.total - countryPage.loaded;
        elements.panelNewsList.insertAdjacentHTML(
            'beforeend',
            `<div class="news-list-footer">
                <span>${escapeHtml(t('showing', { n: filtered.length, total: stories(countryPage.total) }))}</span>
                ${more > 0 ? `<button type="button" class="news-load-more">${escapeHtml(t('loadMore', { n: Math.min(more, PAGE_SIZE) }))}</button>` : ''}
            </div>`,
        );
        const btn = elements.panelNewsList.querySelector('.news-load-more');
        if (btn) {
            btn.addEventListener('click', () => {
                btn.disabled = true;
                btn.textContent = t('loadingShort');
                loadCountryNews(iso2, { append: true });
            });
        }
    }

    // Tap a summary to read the whole excerpt (it's clamped to 3 lines)
    elements.panelNewsList.querySelectorAll('.news-card-summary').forEach((p) => {
        p.addEventListener('click', (e) => {
            if (e.target.closest('.gl-term')) return; // tapping a glossary term shows its tip
            p.classList.toggle('expanded');
        });
    });

    // "Explain this" and "Share" (features/glossary.js, features/share.js)
    const byId = new Map(filtered.map((a) => [String(Number(a.id) || ''), a]));
    elements.panelNewsList.querySelectorAll('.news-card').forEach((card) => {
        const article = byId.get(card.dataset.articleId);
        if (!article) return;
        const btn = card.querySelector('.news-card-explain');
        const box = card.querySelector('.news-card-explainer');
        btn?.addEventListener('click', () => {
            const open = box.hidden;
            if (open && !box.innerHTML) box.innerHTML = renderExplainer(article);
            box.hidden = !open;
            btn.setAttribute('aria-expanded', String(open));
            btn.textContent = open ? t('hideExplain') : t('explain');
        });
        card.querySelector('.news-card-share')?.addEventListener('click', () =>
            shareStory({ ...article, country_name: countryName(article.country_iso2, state.countries.find((c) => c.iso2 === article.country_iso2)?.name || article.country_name), category_name: catName(article.category_slug, article.category_name) }),
        );
    });
}

/** Plain-language context for a story, from the hand-written glossary. */
function renderExplainer(article) {
    const tr = t;
    const ex = explain(article);
    const terms = ex.terms
        .map(
            (t) => `<p><b>${escapeHtml(t.label)}:</b> ${escapeHtml(t.what)}<br><span class="ex-why">${escapeHtml(tr('whyMatter'))}: ${escapeHtml(t.why)}</span></p>`,
        )
        .join('');
    const topic = ex.topic ? `<p class="ex-topic">${escapeHtml(ex.topic)}</p>` : '';
    return `${terms || ''}${topic}<p class="ex-note">${escapeHtml(ex.disclaimer)}</p>`;
}

// ============================================================
// Follow countries (features/follow.js)
// ============================================================
function syncFollowButton(iso2) {
    const btn = elements.panelFollow;
    if (!btn || !iso2) return;
    const on = isFollowingCountry(iso2);
    btn.textContent = on ? t('following') : t('follow');
    btn.setAttribute('aria-pressed', String(on));
    btn.classList.toggle('on', on);
}

function setupRotateButton() {
    const btn = document.getElementById('auto-rotate-toggle');
    if (!btn) return;
    const sync = () => {
        btn.textContent = state.globe.autoRotate ? t('pauseRotation') : t('resumeRotation');
        btn.setAttribute('aria-pressed', String(!!state.globe.autoRotate));
    };
    btn.addEventListener('click', () => {
        state.globe.autoRotate = !state.globe.autoRotate;
        sync();
    });
    sync();
}

function setupFollow() {
    elements.panelFollow?.addEventListener('click', () => {
        const iso2 = state.ui.selectedCountry;
        if (!iso2) return;
        toggleCountry(iso2);
    });
    onFollowsChange(() => {
        syncFollowButton(state.ui.selectedCountry);
        renderSidebarCountries();
    });
}

/** Only http(s) links, attribute-escaped — feed URLs are third-party input. */
function safeUrl(u) {
    try {
        const parsed = new URL(u);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
        return escapeHtml(parsed.href);
    } catch {
        return null;
    }
}

// ============================================================
// Hover tooltip
// ============================================================

function updateHoverTooltip(iso2) {
    if (iso2 && elements.timeValue) {
        const country = state.countries.find((c) => c.iso2 === iso2);
        if (country) {
            elements.timeValue.textContent = `${country.flag_emoji || ''} ${countryName(country.iso2, country.name)}`;
        }
    } else if (elements.timeValue) {
        elements.timeValue.textContent = formatRelativeTime(state.ui.lastUpdated);
    }
}

// ============================================================
// Data Loading
// ============================================================

async function loadInitialData() {
    try {
        const [countriesRes, categoriesRes] = await Promise.all([
            api.getCountries({ limit: 250 }),
            api.getCategories({ active_only: true }),
        ]);

        actions.setCountries(countriesRes.data || []);
        actions.setCategories(categoriesRes.data || []);

        const [latestRes, breakingRes] = await Promise.all([
            api.getLatestNews(50),
            api.getBreakingNews(20),
        ]);

        actions.setLatestNews(latestRes.data || []);
        actions.setBreakingNews(breakingRes.data || []);
    } catch (err) {
        console.error('[ui] Failed to load data:', err);
        throw err;
    }
}

function windowSince(range) {
    const hours = { '24h': 24, '7d': 168, '30d': 720 }[range];
    return hours ? new Date(Date.now() - hours * 60 * 60 * 1000).toISOString() : null;
}

let statsTimer = null;
async function loadCountryStats() {
    try {
        const since = windowSince(state.controls.timeRange);
        const params = {};
        if (since) params.since = since;
        if (state.controls.categoryFilter !== 'all') params.category = state.controls.categoryFilter;
        const res = await api.getCountryStats(params);
        actions.setCountryStats(res.data || []);
    } catch (err) {
        console.error('[ui] Failed to load country stats:', err);
    }
    // Keep pins fresh as the hourly ingestion lands new stories
    clearTimeout(statsTimer);
    statsTimer = setTimeout(loadCountryStats, 5 * 60 * 1000);
}

function refreshNewsForCurrentView() {
    if (state.ui.selectedCountry) {
        loadCountryNews(state.ui.selectedCountry);
    }
}

// Paging state for the open country panel
const PAGE_SIZE = 100;
let countryPage = { key: null, total: 0, loaded: 0, loading: false };

async function loadCountryNews(iso2, { append = false } = {}) {
    const key = `${iso2}|${state.controls.timeRange}|${state.controls.categoryFilter}`;
    if (append && (countryPage.key !== key || countryPage.loading)) return;
    if (!append) countryPage = { key, total: 0, loaded: 0, loading: false };
    countryPage.loading = true;
    try {
        const params = { limit: PAGE_SIZE, offset: append ? countryPage.loaded : 0 };

        if (state.controls.timeRange !== 'all') {
            const hours = { '24h': 24, '7d': 168, '30d': 720 }[state.controls.timeRange];
            if (hours) {
                params.since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
            }
        }

        if (state.controls.categoryFilter !== 'all') {
            params.category = state.controls.categoryFilter;
        }

        const res = await api.getNewsByCountry(iso2, params);
        if (countryPage.key !== key) return; // filters changed mid-flight
        const page = res.data || [];
        const prev = append ? state.news.byCountry.get(iso2.toUpperCase()) || [] : [];
        actions.setCountryNews(iso2, [...prev, ...page]);
        countryPage.loaded = prev.length + page.length;
        countryPage.total = res.pagination?.total ?? countryPage.loaded;
        // Map.set isn't tracked by the store, so re-render explicitly — but
        // only if the user hasn't moved on to another country meanwhile.
        if (state.ui.selectedCountry === iso2.toUpperCase()) {
            renderCountryNews(iso2);
        }
    } catch (err) {
        console.error('[ui] Failed to load country news:', err);
    } finally {
        if (countryPage.key === key) countryPage.loading = false;
    }
}

// ============================================================
// Helpers
// ============================================================

function filterNews(news) {
    let filtered = [...news];

    if (state.controls.timeRange !== 'all') {
        const hours = { '24h': 24, '7d': 168, '30d': 720 }[state.controls.timeRange];
        if (hours) {
            const cutoff = Date.now() - hours * 60 * 60 * 1000;
            filtered = filtered.filter((a) => new Date(a.source_published_at).getTime() >= cutoff);
        }
    }

    if (state.controls.categoryFilter !== 'all') {
        filtered = filtered.filter((a) => a.category_slug === state.controls.categoryFilter);
    }

    filtered.sort((a, b) => b.importance_score - a.importance_score);

    return filtered;
}

function formatRelativeTime(isoString) {
    if (!isoString) return '—';
    const date = new Date(isoString);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('justNow');
    if (diffMins < 60) return t('minAgo', { n: diffMins });
    if (diffHours < 24) return t('hAgo', { n: diffHours });
    if (diffDays < 7) return t('dAgo', { n: diffDays });
    return date.toLocaleDateString();
}

function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    // innerHTML escapes & < > but not quotes; escape those too so the result
    // is also safe inside attribute values.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}


// Make available globally for debugging
window.__UI__ = { elements, actions };

export { loadInitialData };
